<template>
  <div class="product-item">
    <a
      :href="`/product/${product.id}/c/${product.collection_id}`"
      target="_blank"
      class="product-item-link"
      @click="trackProductClick"
    >
      <img
        class="product-image"
        :src="product.image || defaultImage"
        :alt="`product image of style code: ${product.style_code || 'Unknown style code'}`"
        @error="handleImageError"
      >
      <div class="text-section">
        <p
          class="product-name"
          :title="product.name"
        >
          {{ product.name }}
        </p>
        <p
          class="product-style-code"
          :title="product.style_code || 'Unknown style code'"
        >
          {{ product.style_code || 'Unknown style code' }}
        </p>
        <div class="color-options-row">
          <span
            v-if="hiddenColorCount(product) > 0 && !showAllColors"
            class="color-more-indicator"
            @click="expandColors"
          >+{{ hiddenColorCount(product) }}</span>
          <span
            v-if="showAllColors"
            class="color-more-indicator"
            @click="collapseColors"
          >&minus;</span>
          <span
            v-if="showAllColors && hiddenColorCount(product) > 0"
            class="color-remaining-indicator"
          >
            +{{ hiddenColorCount(product) }} more
          </span>
          <template
            v-for="circle in renderColorCircles(product)"
          >
            <component
              :is="circle.component"
              :key="circle.key"
              v-bind="circle.props"
            />
          </template>
        </div>
        <div class="price-row">
          <p>WSP:</p>
          <p>
            {{ product.wholesale_price
              ? getCurrencySymbolChar(product.currency) + product.wholesale_price
              : 'Unavailable' }}
          </p>
        </div>
      </div>
    </a>
  </div>
</template>

<script>
import productImage from '@/assets/images/elements/default-product.jpeg'
import { getCurrencySymbolChar } from '@/common-utils'
import { getColorCode, getColorCodeById, getColorNameById } from '@/product-colors'
import constants, { MAX_COLORS_PER_PRODUCT_CARD, MAX_COLORS_EXPANDED } from '@/constants'
import analytics from '@/@core/utils/analytics'
import ColorCircle from './ColorCircle.vue'

export default {
  name: 'ProductItem',
  components: {
    ColorCircle,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultImage: productImage,
      showAllColors: false,
    }
  },
  methods: {
    getCurrencySymbolChar,

    handleImageError(event) {
      if (!event.target.dataset.fallback) {
        event.target.src = this.defaultImage
        event.target.dataset.fallback = 'true'
      }
    },

    getProductColors(product) {
      if (product?.colors && product.colors.length) {
        return product.colors
      }
      const items = product?.items || []
      const uniqueItems = items.filter(
        (item, index, self) => item && !self.slice(0, index).some(i => i.color_family_id === item.color_family_id),
      )
      return uniqueItems.map(item => ({
        colorNames: [item.color],
        colorFamily: getColorNameById(item.color_family_id),
        hexCode: null,
        colorFamilyId: item.color_family_id,
      }))
    },

    renderColorCircles(product) {
      const colors = this.getProductColors(product)

      const makeCircle = (key, colorCode, colorLabel, colorName = '') => ({
        key,
        component: 'color-circle',
        props: {
          colorCode,
          colorName,
          colorLabel,
        },
      })

      if (!colors.length) {
        return [makeCircle('no-color', '#C0C0C0', 'No color', '/')]
      }

      const max = this.showAllColors ? MAX_COLORS_EXPANDED : MAX_COLORS_PER_PRODUCT_CARD
      const visible = colors.slice(0, max)

      return visible.map((productColor, index) => {
        const label = productColor.colorNames.join(', ')
        const hasColorFamilyId = productColor.colorFamilyId !== undefined
        const code = hasColorFamilyId
          ? getColorCodeById(productColor.colorFamilyId)
          : getColorCode(productColor.colorFamily, productColor.hexCode)
        if (code !== null) {
          return makeCircle(index, code, label, productColor.colorFamily || '')
        }
        return makeCircle(index, '#C0C0C0', label, '/')
      })
    },

    hiddenColorCount(product) {
      const total = this.getProductColors(product).length
      if (this.showAllColors) return Math.max(0, total - MAX_COLORS_EXPANDED)
      return Math.max(0, total - MAX_COLORS_PER_PRODUCT_CARD)
    },

    expandColors(event) {
      event.preventDefault()
      event.stopPropagation()
      this.showAllColors = true
    },

    collapseColors(event) {
      event.preventDefault()
      event.stopPropagation()
      this.showAllColors = false
    },

    trackProductClick() {
      analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_CLICKS_PRODUCT, {
        productId: this.product.id,
        productName: this.product.name,
        styleCode: this.product.style_code,
        brand: this.product.brand?.name,
        price: this.product.wholesale_price,
        currency: this.product.currency,
      })
    },
  },
}
</script>

<style scoped lang="scss">
.product-item {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  height: 315px;
  position: relative;
  width: 155px;

  .product-image {
    width: 100%;
    height: 203px;
    border-radius: 6px 6px 0 0;
    object-fit: contain;
  }

  .text-section {
    padding: 10px 10px 0;

    p {
      margin: 0;
    }

    .product-name {
      font-size: 12px;
      font-weight: 700;
      line-height: 18px;
      max-width: 100%;
      height: 36px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .product-style-code {
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
      text-transform: capitalize;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .color-options-row {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-wrap: wrap;
      gap: 4px;
      margin-top: 4px;
      min-height: 14px;
    }

    .color-more-indicator {
      font-size: 11px;
      font-weight: 600;
      color: #555;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 2px;
      cursor: pointer;
      min-width: 18px;
      min-height: 14px;
      border-radius: 8px;
      padding: 1px 4px;
      transition: all 0.2s ease;

      &:hover {
        color: #000;
        background: rgba(0, 0, 0, 0.06);
        transform: scale(1.05);
      }
    }

    .color-remaining-indicator {
      font-size: 10px;
      color: #999;
    }

    .price-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 5px;
      margin-top: 5px;

      p {
        font-size: 12px;
        font-weight: 400;
        line-height: 18px;
      }
    }
  }
}

.product-item-link {
  text-decoration: none !important;
  color: inherit;
}
</style>
